import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import {
  getClientData,
  searchClient,
  getCategoriesByClient,
} from "../../../services/admin/client/client";
import handleError from "../../../utils/handleError";
import { useDispatch, useSelector } from "react-redux";
import ClientTableSkeleton from "../../../components/Skeletons/ClientTableSkeleton";
import _ from "lodash";
import { encrypt } from "../../../utils/commonLogics";
import CategoryModal from "../../../components/admin/adminDialogs/CategoryDialog";
import { ProgressSpinner } from "primereact/progressspinner";
import Pagination from "../../../utils/admin/pagination/Pagination";
import { getCategoriesForXMOT } from "../../../services/xmotSurvey/survey";
import { Dialog } from "primereact/dialog";
import { SET_STUDY_ID } from "../../../utils/constants";

const ClientsList = () => {
  const [clientData, setClientData] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 50;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [categoriesByClient, setCategoriesByClient] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [showCategoryError, setShowCategoryError] = useState(false);
  // State for sorting; initially no sorting applied
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [clientId, setClientID] = useState(null);
  const [showDialogForCategory, setShowDialogForCategory] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState("");
  const hostname = window.location.hostname;
  const handleCreateClient = () => {
    navigate("/admin-panel/clients/create");
  };

  const getCategoryForXMOT = (clientId) => {
    const reqBody = {
      client_id: clientId,
    };
    getCategoriesForXMOT(token, reqBody)
      .then((response) => {
        if (response.status === 200) {
          const categories = response.data.data;
          setCategoryData(categories);
          fetchXMOTCategory(response.data.data);
          // Check if there's only one category and set selectedCategory accordingly
          if (categories.length === 1) {
            const singleCategory = categories[0];
            setSelectedCategory(
              `${singleCategory.category_id}-${singleCategory.study_id}-${singleCategory.category_type}`,
            );
            setSelectedStudy(singleCategory.study_id);
            // setSelectedTriggerType(singleCategory.category_type);
          } else {
            setSelectedCategory(""); // Reset if there are multiple categories
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  };

  useEffect(() => {
    // getCategoryForXMOT();
  }, []);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    const [, studyId, triggerType] = value.split("-");

    setSelectedCategory(value);
    setSelectedStudy(studyId);
    // setSelectedTriggerType(triggerType);
    if (value !== "") {
      setShowCategoryError(false);
    }
    dispatch({
      type: SET_STUDY_ID,
      payload: studyId,
    });
  };
  const fetchXMOTCategory = (allcategories) => {
    const length = allcategories?.length || 0;
    if (length === 0) {
      // alert("No categories available to create a survey.");
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "No categories available to see the session.",
        life: 3000,
      });
    } else if (length === 1) {
      handleResultView(length, allcategories);
    } else {
      setShowDialogForCategory(true);
    }
  };
  const handleViewResultsClick = (clientId) => {
    setClientID(clientId);
    getCategoryForXMOT(clientId);
  };
  const fetchClientData = (pageNum) => {
    setLoading(true);
    const reqBody = {
      id: user.id,
      type: user.type,
      page: pageNum.toString(),
      limit: limit.toString(),
      // No sorting params sent to keep server data order
    };

    getClientData(token, reqBody)
      .then((response) => {
        setClientData(response.data.client_data || []);
        setTotalClients(response.data.total || 0);
        setLoading(false);
        setSearchPerformed(false);
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
        setLoading(false);
      });
  };

  const fetchCategoriesByClient = (id) => {
    setLoadingCategories(true);
    const reqBody = {
      id: user.id,
      type: user.type,
      client_id: id,
    };
    getCategoriesByClient(token, reqBody)
      .then((response) => {
        if (response?.data.success === 1) {
          setCategoriesByClient(response?.data?.client_category_data || []);
          setVisible(true);
        }
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
      })
      .finally(() => {
        setLoadingCategories(false);
      });
  };

  useEffect(() => {
    fetchClientData(page);
  }, [page]);

  const handlePrevPage = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(totalClients / limit);
    if (page < maxPage) setPage((prev) => prev + 1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If already sorting on this field, toggle the order
      const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newSortOrder);
    } else {
      // New sort field selected, set sort order ascending by default
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const renderHeaderAndControls = () => (
    <div
      className={`d-flex justify-content-between align-items-center px-3 py-2 ${
        clientData.length > 0 || loading ? "" : "my-3"
      }`}
    >
      <h6 className="fw-bold m-0 title">View all clients</h6>
      <Button
        label="Create a client"
        className="px-3 py-2 fw-bold createClientBtn"
        onClick={handleCreateClient}
      />
    </div>
  );

  const debouncedSearchClient = useCallback(
    _.debounce((name) => {
      if (name.length >= 3) {
        setLoading(true);
        const formData = new FormData();
        formData.append("name", name.trim());
        formData.append("id", user.id);
        formData.append("type", user.type);
        formData.append("page", page.toString());
        formData.append("limit", limit.toString());
        // No sorting params sent in search to preserve original order

        searchClient(token, formData)
          .then((response) => {
            setClientData(response.data.client_data || []);
            setTotalClients(response.data.total || 0);
            setLoading(false);
            setSearchPerformed(true);
          })
          .catch((error) => {
            handleError(error, navigate, dispatch);
            setLoading(false);
          });
      }
      if (name.length === 0) {
        fetchClientData(1);
      }
    }, 500),
    [token, navigate, dispatch, page],
  );

  const handleSearchClient = (event) => {
    const searchTerm = event.target.value;
    debouncedSearchClient(searchTerm);
  };

  // Memoized sorted clientData for rendering;
  // If no sortField or sortOrder, return data as-is (no sorting)
  const sortedClientData = useMemo(() => {
    if (!clientData) return [];

    if (!sortField || !sortOrder) {
      return clientData;
    }

    const data = [...clientData];
    if (sortField === "client_name") {
      data.sort((a, b) => {
        const nameA = a.client_name?.toLowerCase() || "";
        const nameB = b.client_name?.toLowerCase() || "";
        if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
        if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    } else if (sortField === "categories_count") {
      data.sort((a, b) => {
        const valA = a.categories_count || 0;
        const valB = b.categories_count || 0;
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [clientData, sortField, sortOrder]);

  const renderSearchAndPagination = () => {
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, totalClients);
    const isPrevDisabled = page === 1;
    const isNextDisabled = end >= totalClients;

    return (
      <div className="d-flex justify-content-between align-items-center p-2">
        <div className="d-flex align-items-center">
          <span className="p-input-icon-left">
            <i
              className="pi pi-search"
              style={{ marginLeft: "320px", color: "#9e9e9e" }}
            ></i>
            <InputText
              placeholder="Search for clients"
              style={{ borderRadius: "0px", width: "350px", fontWeight: "400" }}
              className="inputField"
              autoComplete="off"
              onChange={handleSearchClient}
            />
          </span>
        </div>
        <Pagination
          start={start}
          end={end}
          totalRecord={totalClients}
          isPrevDisabled={isPrevDisabled}
          isNextDisabled={isNextDisabled}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
        />
      </div>
    );
  };

  const handleResultView = (length, allcategories) => {
    if (length === 1) {
      const singleCategoryID = allcategories[0].study_id.toString();
      const path = `/admin-panel/clients/view-results?studyId=${encrypt(singleCategoryID)}`;
      navigate(path);
    } else {
      if (!selectedCategory) {
        setShowCategoryError(true);
        return;
      }
      const path = `/admin-panel/clients/view-results?studyId=${encrypt(selectedStudy)}`;
      navigate(path);
    }
  };

  const footerContent = (
    <div>
      <Button
        label="Next"
        className="px-3 py-2 fw-bold createClientBtn"
        onClick={handleResultView}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <CategoryModal
        visible={visible}
        setVisible={setVisible}
        categoriesByClient={categoriesByClient}
      />
      {loadingCategories && (
        <div className="spinnerParent">
          <ProgressSpinner />
        </div>
      )}
      <div className="container-fluid page-wrapper">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card shadow-card" style={{ minHeight: "590px" }}>
              {renderHeaderAndControls()}
              <Divider
                style={{
                  margin: "0.5rem 0 !important",
                  padding: "0 1rem !important",
                }}
              />
              {loading || sortedClientData.length > 0 || searchPerformed ? (
                <div className="table-responsive">
                  {renderSearchAndPagination()}
                  <Divider />
                  <table className="table table-borderless clientsTable">
                    <thead className="clientsTableHead">
                      <tr className="text-muted" style={{ fontSize: "12px" }}>
                        <th style={{ width: "6%" }}>Sr #</th>
                        <th
                          style={{ width: "30%", cursor: "pointer" }}
                          onClick={() => handleSort("client_name")}
                        >
                          Client Name{" "}
                          {sortField === "client_name" ? (
                            sortOrder === "asc" ? (
                              <span className="pi pi-sort-amount-down"></span>
                            ) : (
                              <span className="pi pi-sort-amount-up"></span>
                            )
                          ) : (
                            <span className="pi pi-sort-alt"></span>
                          )}
                        </th>
                        <th>City</th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("categories_count")}
                        >
                          Categories{" "}
                          {sortField === "categories_count" ? (
                            sortOrder === "asc" ? (
                              <span className="pi pi-sort-amount-down"></span>
                            ) : (
                              <span className="pi pi-sort-amount-up"></span>
                            )
                          ) : (
                            <span className="pi pi-sort-alt"></span>
                          )}
                        </th>
                        {/* <th>Analyze</th> */}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <ClientTableSkeleton />
                      ) : sortedClientData.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-muted">
                            No records found
                          </td>
                        </tr>
                      ) : (
                        sortedClientData.map((client, index) => (
                          <tr key={client.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{client.client_name}</td>
                            <td>{client.city_name}</td>
                            <td>
                              <span
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  fetchCategoriesByClient(client.id)
                                }
                              >
                                {client.categories_count}
                              </span>
                            </td>
                            {/* <td>
                              <span
                                className="text-primary"
                                style={{
                                  cursor: "pointer",
                                  marginRight: "10px",
                                }}
                                onClick={() =>handleViewResultsClick(client.id)}
                              >
                                View Results
                              </span>
                            </td> */}
                            <td>
                              <span
                                className="text-primary"
                                style={{
                                  cursor: "pointer",
                                  marginRight: "10px",
                                }}
                                onClick={() =>
                                  navigate(
                                    `/admin-panel/clients/edit/${encrypt(client.id.toString())}`,
                                  )
                                }
                              >
                                Edit
                              </span>
                              <span
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  navigate(
                                    `/admin-panel/categories/fromClient-${client.id}`,
                                  );
                                  // setTimeout(
                                  //   () => window.location.reload(),
                                  //   100,
                                  // );
                                }}
                              >
                                | View Dashboard
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className="d-flex flex-column justify-content-center align-items-center text-center"
                  style={{ height: "520px" }}
                >
                  <p className="text-muted fw-semibold mb-4">
                    No clients have been added yet. Use the button <br /> below
                    to create a new client.
                  </p>
                  <Button
                    label="Create a client"
                    className="px-4 py-2 fw-bold createClientBtn"
                    onClick={handleCreateClient}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog
        visible={categoryData?.length > 1 && showDialogForCategory}
        modal
        header="Select Category"
        footer={footerContent}
        style={{ width: "30rem" }}
        onHide={() => setShowDialogForCategory(false)}
      >
        <div className="container p-5">
          <label className="pb-1">Select Category</label>
          <select
            className="form-select"
            id="categoryDropdown"
            onChange={handleCategoryChange}
            value={selectedCategory}
          >
            <option value="">Please select a category</option>
            {categoryData?.map((item) => (
              <option
                key={item.category_id}
                value={
                  item.category_id +
                  "-" +
                  item.study_id +
                  "-" +
                  item.category_type
                }
              >
                {item.category_name}
              </option>
            ))}
          </select>
          {showCategoryError && (
            <small className="text-danger">Please select the category</small>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default ClientsList;
